<template>
  <div class="min-h-screen py-6">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
              <i class="mdi mdi-star-circle text-white text-xl"></i>
            </div>
            <h2 class="text-2xl font-bold text-gray-900">{{ $t('loyalty.name') }}</h2>
          </div>

          <!-- Store Filter: superadmin only -->
          <div v-if="isSuperAdmin" class="flex items-center space-x-2">
            <label class="text-sm font-medium text-gray-600">{{ $t('loyalty.label.store') }}:</label>
            <el-select
              v-model="selectedStoreId"
              :placeholder="$t('loyalty.label.all_stores')"
              clearable
              size="small"
              style="width: 200px">
              <el-option
                v-for="store in stores"
                :key="store.id"
                :label="store.name"
                :value="store.id">
              </el-option>
            </el-select>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto" v-loading="loading">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('loyalty.table.name') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('loyalty.label.description') }}
                </th>
                <th v-if="isSuperAdmin" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('loyalty.label.store') }}
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('loyalty.table.points_per_currency') }}
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('loyalty.table.currency') }}
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('loyalty.label.expiry_days') }}
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('loyalty.table.status') }}
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50">
                  {{ $t('loyalty.table.actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="program in filteredPrograms" :key="program.id" class="hover:bg-gray-50 transition-colors">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ program.name }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-500 max-w-xs truncate">{{ program.description || '-' }}</div>
                </td>
                <td v-if="isSuperAdmin" class="px-6 py-4 whitespace-nowrap">
                  <span class="text-sm text-gray-700">{{ storeNameById(program.store_id) }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="text-sm font-semibold text-gray-900">
                    {{ parseFloat(program.points_per_currency).toFixed(2) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {{ program.currency_code }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="text-sm text-gray-500">
                    {{ program.points_expiry_days || $t('message.no_data') }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span
                    :class="program.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                    {{ program.is_active ? $t('loyalty.status.active') : $t('loyalty.status.inactive') }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right sticky right-0 bg-white">
                  <div class="flex items-center justify-end space-x-2">
                    <button
                      @click="editProgram(program)"
                      class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
                      <i class="mdi mdi-pencil mr-1.5 text-sm"></i>
                      {{ $t('button.edit') }}
                    </button>
                    <button
                      @click="toggleActive(program)"
                      :class="program.is_active ? 'text-orange-600 hover:bg-orange-50 border-orange-300' : 'text-green-600 hover:bg-green-50 border-green-300'"
                      class="inline-flex items-center px-3 py-1.5 border shadow-sm text-sm font-medium rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors">
                      <i :class="program.is_active ? 'mdi mdi-eye-off' : 'mdi mdi-eye'" class="mr-1.5 text-sm"></i>
                      {{ program.is_active ? $t('loyalty.button.deactivate') : $t('loyalty.button.activate') }}
                    </button>
                    <button
                      @click="viewProgram(program.id)"
                      class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
                      <i class="mdi mdi-eye mr-1.5 text-sm"></i>
                      {{ $t('button.view') }}
                    </button>
                    <button
                      @click="handleDeleteProgram(program.id)"
                      class="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors">
                      <i class="mdi mdi-delete mr-1.5 text-sm"></i>
                      {{ $t('button.delete') }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredPrograms.length === 0">
                <td :colspan="isSuperAdmin ? 8 : 7" class="px-6 py-12 text-center">
                  <div class="text-gray-500">
                    <i class="mdi mdi-information-outline text-4xl mb-2"></i>
                    <p class="text-sm">{{ $t('message.no_data') }}</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'LoyaltyProgramList',
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      programs: [],
      stores: [],
      selectedStoreId: null,
      loading: false
    }
  },
  computed: {
    isSuperAdmin () {
      const user = this.$store.getters['Auth/getUser']
      return !!(user && user.is_superadmin)
    },
    storeMap () {
      const map = {}
      this.stores.forEach(s => { map[s.id] = s.name })
      return map
    },
    filteredPrograms () {
      if (!this.isSuperAdmin || !this.selectedStoreId) return this.programs
      return this.programs.filter(p => p.store_id === this.selectedStoreId)
    }
  },
  async mounted () {
    await this.loadPrograms()
    if (this.isSuperAdmin) {
      this.loadStores()
    }
  },
  methods: {
    async loadPrograms () {
      try {
        this.loading = true
        this.programs = await this.$store.dispatch('loyalty/programs/fetchPrograms')
      } catch (error) {
        console.error('Error fetching programs:', error)
      } finally {
        this.loading = false
      }
    },
    async loadStores () {
      try {
        this.stores = await this.$departmentService.getDepartments() || []
      } catch (error) {
        console.error('Error fetching stores:', error)
      }
    },
    storeNameById (storeId) {
      if (!storeId) return '-'
      return this.storeMap[storeId] || `#${storeId}`
    },
    editProgram (program) {
      this.$emit('program-selected', program.id)
      this.$emit('edit-program', program)
    },
    viewProgram (programId) {
      this.$router.push({
        name: 'loyalty-program-manage',
        params: { id: programId }
      })
    },
    async toggleActive (program) {
      try {
        await this.withLoading(async () => {
          await this.$store.dispatch('loyalty/programs/updateProgram', {
            id: program.id,
            data: { is_active: !program.is_active }
          })
          await this.loadPrograms()
          this.showSuccess(this.$t('loyalty.message.updated'))
        }, 'loading')
      } catch (error) {
        console.error('Error toggling program status:', error)
      }
    },
    async handleDeleteProgram (id) {
      this.askQuestion(this.$t('loyalty.confirm.delete'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$store.dispatch('loyalty/programs/deleteProgram', id)
            await this.loadPrograms()
            this.showSuccess(this.$t('loyalty.message.deleted'))
            this.$emit('program-deleted', id)
          }, 'loading')
        } catch (error) {
          console.error('Error deleting program:', error)
        }
      })
    }
  }
}
</script>

<style scoped>
</style>
